// The auth client: the four-token (§7.2-style) scheme lives entirely here, so
// screens never touch it. Mirrors Monza's client, adapted to Yoreflix's REST
// endpoints (path-per-action, GET boot probe, body-or-header CSRF capture).
//
//   - The HttpOnly cookies (session / rt / ct / ik) flow automatically; JS never
//     sees them.
//   - The CSRF token is captured from every response (the rotated X-Yoreflix-CSRF
//     header when readable, else the body's Data.CSRFToken) and sent back on
//     every GUARDED request (it is coupled to the RequestToken, which rotates on
//     each request).
//   - 428 (stale request token) / 409 (replay) trigger a single-flight refresh
//     (POST /auth/refresh, uses the ct cookie) + one retry — transparent to
//     callers. Concurrent staleness shares one refresh.
//   - 401 (no session / refresh denied) fires onUnauthorized so the app flushes
//     state and drops to sign-in. The boot probe's own 401 is expected and does
//     NOT fire it.
//   - Every request is serialized: the RequestToken is single-use and rotates per
//     request, so overlapping calls would race the rotation (the straggler 428s).
//     One in flight at a time means each picks up the freshly rotated token.

import { request, CSRF_HEADER } from './transport'
import { AccountDeletedError, StaleTokenError, UnauthorizedError } from './errors'
import type { ApiEnvelope, AuthData, AuthUser, SignInData } from './types'

export interface AuthClient {
  /** Boot probe (GET /auth/session): the live user, or null when not signed in (401). */
  bootProbe(): Promise<AuthUser | null>
  /** Authenticate. Mints the session/rt/ct/ik cookies + CSRF. Throws UnauthorizedError on bad credentials. */
  signIn(email: string, password: string): Promise<AuthUser>
  /**
   * Self-serve signup (POST /auth/register). Creates a free-tier account and, on
   * success, signs it in exactly like signIn (same cookies + CSRF) — so the caller can
   * go straight to checkout. Throws ApiError(409) if the email is taken, ApiError(400)
   * on a bad email/short password, ApiError(429) when throttled.
   */
  signUp(email: string, password: string, displayName?: string): Promise<AuthUser>
  /** Re-establish a session from the ct cookie. Throws UnauthorizedError when denied. */
  refresh(): Promise<AuthUser>
  /** Revoke the session and clear local auth state. */
  signOut(): Promise<void>
  /** Guarded echo of the current user — handy for proving token rotation. */
  whoami(): Promise<AuthUser>
  /** Low-level guarded call (CSRF header + refresh/retry). For future /api endpoints. */
  request<T>(method: 'GET' | 'POST', path: string, body?: Record<string, unknown>): Promise<T>
  /** Register the handler fired on an unexpected 401 (expiry / revocation / refresh denied). */
  onUnauthorized(handler: (() => void) | null): void
  /** The current in-memory user (null until a successful signin/refresh/probe). */
  currentUser(): AuthUser | null
  /** The current in-memory CSRF token (diagnostic). */
  csrfToken(): string | null
}

export function createAuthClient(): AuthClient {
  const state: {
    csrf: string | null
    user: AuthUser | null
    onUnauthorized: (() => void) | null
  } = { csrf: null, user: null, onUnauthorized: null }

  // Capture the rotated CSRF from a response: prefer the header (freshest, set on
  // every authed response once exposed cross-origin), fall back to the body token
  // (signin/refresh/session always carry it — works even before the header is
  // CORS-exposed).
  function captureCsrf(res: Response, envelope: ApiEnvelope<unknown>): void {
    const fromHeader = res.headers.get(CSRF_HEADER)
    if (fromHeader) {
      state.csrf = fromHeader
      return
    }
    const data = envelope.Data as AuthData | null
    if (data && typeof data.CSRFToken === 'string') state.csrf = data.CSRFToken
  }

  interface FireOpts {
    /** Attach the X-Yoreflix-CSRF header (guarded endpoints only). */
    guarded?: boolean
    /** Don't fire onUnauthorized on a 401 (the boot probe / signin attempt). */
    expect401?: boolean
  }

  /** One HTTP attempt: sends the current CSRF on guarded calls, captures the rotated one. */
  function fire<T>(
    method: 'GET' | 'POST',
    path: string,
    body: Record<string, unknown> | undefined,
    opts: FireOpts,
  ): Promise<T> {
    const headers: Record<string, string> = {}
    if (opts.guarded && state.csrf) headers[CSRF_HEADER] = state.csrf

    return request<T>(path, {
      method,
      body,
      headers,
      onResponse: captureCsrf,
    }).catch((err: unknown) => {
      if (err instanceof UnauthorizedError && !opts.expect401) state.onUnauthorized?.()
      throw err
    })
  }

  // Single-flight refresh: concurrent staleness awaits one shared RefreshSession.
  let refreshInFlight: Promise<AuthUser> | null = null
  function refresh(): Promise<AuthUser> {
    if (!refreshInFlight) {
      refreshInFlight = fire<AuthData>('POST', 'auth/refresh', undefined, {})
        .then((data) => {
          state.user = data.User
          return data.User
        })
        .finally(() => {
          refreshInFlight = null
        })
    }
    return refreshInFlight
  }

  // Serialize every call so the single-use RequestToken can't be raced.
  let tail: Promise<unknown> = Promise.resolve()
  function serialized<T>(task: () => Promise<T>): Promise<T> {
    const result = tail.then(task, task)
    tail = result.then(
      () => undefined,
      () => undefined,
    )
    return result
  }

  /** A guarded call with the auth lifecycle: serialized, refresh-once-and-retry on 428/409. */
  function guardedCall<T>(method: 'GET' | 'POST', path: string, body?: Record<string, unknown>): Promise<T> {
    return serialized(async () => {
      try {
        return await fire<T>(method, path, body, { guarded: true })
      } catch (err) {
        if (err instanceof StaleTokenError) {
          await refresh() // throws (401) if denied -> onUnauthorized already fired, propagates
          return await fire<T>(method, path, body, { guarded: true }) // retry once
        }
        throw err
      }
    })
  }

  return {
    // ★ SERIALIZED like every other call — this is what stops the boot 428. The single-use RequestToken
    // rotates per request, and on the TV the session probe and the per-user rail calls (/watch/continue,
    // /watch/list) fire on the same mount; an UNserialized probe raced the first guarded call and the
    // straggler drew a 428 → an extra refresh+retry on every boot. Queuing the probe (it still runs
    // first — its effect mounts first) settles the rotated _rt cookie before the guarded calls fire. No
    // deadlock: the probe is a top-level call, never issued from inside another serialized task.
    bootProbe: () =>
      serialized(async () => {
        try {
          const data = await fire<AuthData>('GET', 'auth/session', undefined, { expect401: true })
          state.user = data.User
          return data.User
        } catch (err) {
          if (err instanceof UnauthorizedError) {
            state.user = null
            return null // expected "no session" signal
          }
          throw err
        }
      }),
    signIn: async (email, password) => {
      // Auth primitive: a 401 here is bad credentials, not a stale token. Don't
      // fire the global onUnauthorized for a deliberate login attempt.
      const data = await fire<SignInData>(
        'POST',
        'auth/signin',
        { EmailAddress: email, Password: password },
        { expect401: true },
      )
      /*
       * ⛔ THE PASSWORD WAS RIGHT AND THE ACCOUNT IS GONE — a deletion the customer booked, whose
       *    date arrived; this request is what carried it out (M0049). The server says so with a 200
       *    and no `User`, because 401 would read as "wrong password".
       *
       * ⚠ CHECKED BEFORE `data.User` IS TOUCHED. Without this the assignment below stores
       *   `undefined` as the signed-in user and returns it, and every caller believes it succeeded —
       *   a sign-in screen that "worked" onto a blank app.
       */
      if (data.AccountDeleted === true || !data.User) {
        state.user = null
        throw new AccountDeletedError(
          typeof data.Message === 'string' && data.Message !== ''
            ? data.Message
            : 'This account has been deleted.',
        )
      }
      state.user = data.User
      return data.User
    },
    signUp: async (email, password, displayName) => {
      // Like signIn, this is a deliberate auth attempt: a 4xx (409 taken / 400 invalid /
      // 429 throttled) is a user-facing error, not a session-expiry signal, so don't fire
      // the global onUnauthorized. On 201 the session cookies + CSRF are already set.
      const data = await fire<AuthData>(
        'POST',
        'auth/register',
        { EmailAddress: email, Password: password, DisplayName: displayName ?? '' },
        { expect401: true },
      )
      state.user = data.User
      return data.User
    },
    refresh,
    signOut: async () => {
      await guardedCall('POST', 'auth/signout')
      state.csrf = null
      state.user = null
    },
    whoami: () => guardedCall<AuthData>('GET', 'auth/whoami').then((d) => d.User),
    request: (method, path, body) => guardedCall(method, path, body),
    onUnauthorized: (handler) => {
      state.onUnauthorized = handler
    },
    currentUser: () => state.user,
    csrfToken: () => state.csrf,
  }
}

/** The shared singleton the whole SPA uses. */
export const authClient = createAuthClient()
